import { Injectable } from '@angular/core';

import { Observable, filter, map } from 'rxjs';

import { WebsocketFactory, WebsocketClient, socketUrls } from '@personal-area/global/websocket';
import {
  Account,
  WebsocketEventType,
  WebsocketEvent,
  AccountCreatedEvent,
  AccountUpdatedEvent,
  AccountInfoUpdatedEvent,
  AccountInfo,
  AccountBalanceEvent,
  AccountLeverageEvent,
  AccountSwapFreeEvent,
  PrimaryAccount,
  CertificateModel,
  CertificateEvent,
} from '@personal-area/global/accounts/models';

@Injectable({
  providedIn: 'root',
})
export class AccountSocketService {

  private client: WebsocketClient;

  constructor(
    private readonly _factory: WebsocketFactory
  ) {}

  public connect() {
    this.client = this._factory
      .create(socketUrls.base, 'user')
      .useAuth();

    this.client.init();
  }

  public get accountCreated(): Observable<Account> {
    return this.client.output<{ result: WebsocketEvent<AccountCreatedEvent> }>().pipe(
      filter(event => event.result.eventType === WebsocketEventType.AccountCreated),
      map(event => event.result.response.account)
    );
  }

  public get accountUpdated(): Observable<Account> {
    return this.client.output<{ result: WebsocketEvent<AccountUpdatedEvent> }>().pipe(
      filter(event => event.result.eventType === WebsocketEventType.AccountUpdated),
      map(event => event.result.response.account)
    );
  }

  public get accountInfoUpdated(): Observable<AccountInfo> {
    return this.client.output<{ result: WebsocketEvent<AccountInfoUpdatedEvent> }>().pipe(
      filter(event => event.result.eventType === WebsocketEventType.AccountInfoUpdated),
      map(event => event.result.response.accountInfoUpdated)
    );
  }

  public get accountBalance(): Observable<AccountBalanceEvent> {
    return this.client.output<{ result: WebsocketEvent<AccountBalanceEvent> }>().pipe(
      filter(event => event.result.eventType === WebsocketEventType.AccountBalance),
      map(event => event.result.response)
    );
  }

  public get accountLeverage(): Observable<AccountLeverageEvent> {
    return this.client.output<{ result: WebsocketEvent<AccountLeverageEvent> }>().pipe(
      filter(event => event.result.eventType === WebsocketEventType.AccountLeverage),
      map(event => event.result.response)
    );
  }

  public get primaryAccountID(): Observable<number> {
    return this.client.output<{ result: WebsocketEvent<PrimaryAccount> }>().pipe(
      filter(event => event.result.eventType === WebsocketEventType.PrimaryAccountUpdated),
      map(event => event.result.response.primaryAccount.primaryAccountId)
    );
  }

  public get accountSwapFree(): Observable<AccountSwapFreeEvent> {
    return this.client.output<{ result: WebsocketEvent<AccountSwapFreeEvent> }>().pipe(
      filter(event => event.result.eventType === WebsocketEventType.AccountSwapFree),
      map(event => event.result.response)
    );
  }

  public get certificate(): Observable<CertificateModel> {
    return this.client
      .output<{ result: WebsocketEvent<CertificateEvent> }>()
      .pipe(
        filter(
          (event) => event.result.eventType === WebsocketEventType.Certificate,
        ),
        map((event) => event.result.response.certificate),
      );
  }
}
